import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  FileUpload as UploadIcon,
  Today as TodayIcon,
  LocalShipping as ContainerIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../config/api';

interface Container {
  id: number;
  container_no: string;
  seal_no: string;
  po_number: string;
  delivery_day: string;
  delivery_time: string;
  dock: string;
  location: string;
  status: 'in_transit' | 'received';
  prepopulated_by_staff_no: string;
  created_at: string;
  updated_at: string;
  line_count: number;
}

interface ContainerLine {
  id: number;
  container_id: number;
  stock_code: string;
  rolls_per_pallet: number;
  sheets: number;
  metres: number;
  certification: string;
  prepopulated_qty: number;
}

const ContainerManagement: React.FC = () => {
  const { user } = useAuth();
  const [containers, setContainers] = useState<Container[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContainer, setEditingContainer] = useState<Container | null>(null);
  const [linesDialog, setLinesDialog] = useState(false);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [containerLines, setContainerLines] = useState<ContainerLine[]>([]);
  const [newLine, setNewLine] = useState({
    stock_code: '',
    rolls_per_pallet: 0,
    sheets: 0,
    metres: 0,
    certification: '',
    prepopulated_qty: 0
  });
  const [filters, setFilters] = useState({
    date: 'today',
    dock: '',
    status: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    container_no: '',
    seal_no: '',
    po_number: '',
    delivery_day: new Date().toISOString().split('T')[0],
    delivery_time: '08:00',
    dock: '',
    location: ''
  });

  // Use API_URL which includes /api path

  // Dock options with building mappings
  const dockOptions = [
    { value: 'Dock 1', label: 'Dock 1 (Building 3)' },
    { value: 'Dock 2', label: 'Dock 2 (Building 4)' },
    { value: 'Dock 3', label: 'Dock 3 (Building 2)' },
    { value: 'Dock 4', label: 'Dock 4 (Building 1)' }
  ];

  const getDockDisplayName = (dockValue: string) => {
    const dock = dockOptions.find(d => d.value === dockValue);
    return dock ? dock.label : dockValue;
  };

  useEffect(() => {
    loadContainers();
  }, [filters]);

  const loadContainers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.date) params.append('date', filters.date);
      if (filters.dock) params.append('dock', filters.dock);
      if (filters.status) params.append('status', filters.status);

      const response = await api.get(`/containers?${params}`);
      if (response.data.success) {
        setContainers(response.data.containers);
      } else {
        setError('Failed to load containers');
      }
    } catch (err) {
      setError('Error loading containers');
      console.error('Error loading containers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        prepopulated_by_staff_no: user?.staff_no || 'MATERIALS_TEAM'
      };

      if (editingContainer) {
        // Update existing container
        await api.put(`/containers/${editingContainer.id}`, payload);
      } else {
        // Create new container
        await api.post('/containers', payload);
      }

      setOpenDialog(false);
      setEditingContainer(null);
      resetForm();
      loadContainers();
    } catch (err) {
      setError('Error saving container');
      console.error('Error saving container:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      container_no: '',
      seal_no: '',
      po_number: '',
      delivery_day: new Date().toISOString().split('T')[0],
      delivery_time: '08:00',
      dock: '',
      location: ''
    });
  };

  const handleEdit = (container: Container) => {
    setEditingContainer(container);
    setFormData({
      container_no: container.container_no,
      seal_no: container.seal_no || '',
      po_number: container.po_number || '',
      delivery_day: container.delivery_day,
      delivery_time: container.delivery_time || '08:00',
      dock: container.dock || '',
      location: container.location || ''
    });
    setOpenDialog(true);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' => {
    switch (status) {
      case 'in_transit': return 'primary';
      case 'received': return 'success';
      default: return 'default';
    }
  };

  // Legacy function - can be removed in cleanup phase
  const canMoveToInProgress = (container: Container) => {
    return false; // No longer applicable with simplified status
  };

  // Legacy function - can be removed in cleanup phase
  const moveToInProgress = async (container: Container) => {
    // No longer applicable with simplified status
  };

  const loadContainerLines = async (container: Container) => {
    try {
      const response = await api.get(`/containers/${container.id}/lines`);
      if (response.data.success) {
        setContainerLines(response.data.lines);
        setSelectedContainer(container);
        setLinesDialog(true);
      } else {
        setError('Failed to load container lines');
      }
    } catch (err) {
      setError('Error loading container lines');
    }
  };

  const addContainerLine = async () => {
    if (!selectedContainer || !newLine.stock_code) {
      setError('Stock code is required');
      return;
    }

    try {
      await api.post(`/containers/${selectedContainer.id}/lines`, {
        lines: [newLine]
      });

      // Reset form
      setNewLine({
        stock_code: '',
        rolls_per_pallet: 0,
        sheets: 0,
        metres: 0,
        certification: '',
        prepopulated_qty: 0
      });

      // Reload lines
      await loadContainerLines(selectedContainer);
      // Reload containers to update line count
      loadContainers();
    } catch (err) {
      setError('Error adding container line');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" component="h1" gutterBottom>
              <ContainerIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Container Management
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingContainer(null);
                resetForm();
                setOpenDialog(true);
              }}
            >
              New Container
            </Button>
          </Box>
        </Grid>

        {/* Filters */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Date Filter</InputLabel>
                    <Select
                      value={filters.date}
                      onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                    >
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="next_week">Next Week</MenuItem>
                      <MenuItem value="next_two_weeks">Next Two Weeks</MenuItem>
                      <MenuItem value="next_month">Next Month</MenuItem>
                      <MenuItem value="">All Dates</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Dock</InputLabel>
                    <Select
                      value={filters.dock}
                      onChange={(e) => setFilters({ ...filters, dock: e.target.value })}
                    >
                      <MenuItem value="">All Docks</MenuItem>
                      {dockOptions.map((dock) => (
                        <MenuItem key={dock.value} value={dock.value}>{dock.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="in_transit">In Transit</MenuItem>
                      <MenuItem value="received">Received</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    startIcon={<TodayIcon />}
                    onClick={() => setFilters({ date: 'today', dock: '', status: '' })}
                    fullWidth
                  >
                    Today's Containers
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Error Alert */}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Grid>
        )}

        {/* Containers Table */}
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Container No</TableCell>
                  <TableCell>Seal No</TableCell>
                  <TableCell>PO No</TableCell>
                  <TableCell>Delivery</TableCell>
                  <TableCell>Dock</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Lines</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {containers.map((container) => (
                  <TableRow key={container.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {container.container_no}
                      </Typography>
                    </TableCell>
                    <TableCell>{container.seal_no || '-'}</TableCell>
                    <TableCell>{container.po_number || '-'}</TableCell>
                    <TableCell>
                      {container.delivery_day}
                      {container.delivery_time && (
                        <Typography variant="caption" display="block">
                          {container.delivery_time}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{container.dock ? getDockDisplayName(container.dock) : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={container.status}
                        color={getStatusColor(container.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{container.line_count || 0}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <IconButton size="small" onClick={() => handleEdit(container)}>
                          <EditIcon />
                        </IconButton>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => loadContainerLines(container)}
                          color="secondary"
                        >
                          Lines ({container.line_count})
                        </Button>
                        {canMoveToInProgress(container) && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => moveToInProgress(container)}
                          >
                            Start
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingContainer ? 'Edit Container' : 'Create New Container'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Container Number"
                value={formData.container_no}
                onChange={(e) => setFormData({ ...formData, container_no: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Seal Number"
                value={formData.seal_no}
                onChange={(e) => setFormData({ ...formData, seal_no: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.po_number}
                onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Dock</InputLabel>
                <Select
                  value={formData.dock}
                  onChange={(e) => setFormData({ ...formData, dock: e.target.value })}
                >
                  {dockOptions.map((dock) => (
                    <MenuItem key={dock.value} value={dock.value}>{dock.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Delivery Date"
                value={formData.delivery_day}
                onChange={(e) => setFormData({ ...formData, delivery_day: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="time"
                label="Delivery Time"
                value={formData.delivery_time}
                onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Delivery location or special instructions"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingContainer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Container Lines Management Dialog */}
      <Dialog open={linesDialog} onClose={() => setLinesDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Manage Container Lines
          {selectedContainer && (
            <Typography variant="subtitle2" color="textSecondary">
              Container: {selectedContainer.container_no}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Existing Lines */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Existing Lines ({containerLines.length})
              </Typography>
              {containerLines.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Stock Code</TableCell>
                        <TableCell>Rolls/Pallet</TableCell>
                        <TableCell>Sheets</TableCell>
                        <TableCell>Metres</TableCell>
                        <TableCell>Certification</TableCell>
                        <TableCell>Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {containerLines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.stock_code}</TableCell>
                          <TableCell>{line.rolls_per_pallet || '-'}</TableCell>
                          <TableCell>{line.sheets || '-'}</TableCell>
                          <TableCell>{line.metres || '-'}</TableCell>
                          <TableCell>{line.certification || '-'}</TableCell>
                          <TableCell>{line.prepopulated_qty || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No lines added yet</Alert>
              )}
            </Grid>

            {/* Add New Line */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Add New Line
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Stock Code"
                    value={newLine.stock_code}
                    onChange={(e) => setNewLine({ ...newLine, stock_code: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Rolls/Pallet"
                    type="number"
                    value={newLine.rolls_per_pallet}
                    onChange={(e) => setNewLine({ ...newLine, rolls_per_pallet: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Sheets"
                    type="number"
                    value={newLine.sheets}
                    onChange={(e) => setNewLine({ ...newLine, sheets: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Metres"
                    type="number"
                    value={newLine.metres}
                    onChange={(e) => setNewLine({ ...newLine, metres: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Certification"
                    value={newLine.certification}
                    onChange={(e) => setNewLine({ ...newLine, certification: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={1}>
                  <TextField
                    fullWidth
                    label="Qty"
                    type="number"
                    value={newLine.prepopulated_qty}
                    onChange={(e) => setNewLine({ ...newLine, prepopulated_qty: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={addContainerLine}
                    disabled={!newLine.stock_code}
                    startIcon={<AddIcon />}
                  >
                    Add Line
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContainerManagement;